import React, { useMemo } from 'react';

/* La forma de la rejilla. Se exporta porque quien anima el muro necesita las
   mismas cifras para escalonar el barrido desde el centro hacia fuera. Impar
   en las filas, para que exista un centro exacto. */
export const FILAS = 7;
export const POR_FILA = 9;

interface Props {
    /** Lo que se va a tachar. */
    palabras: string[];
    /** Lo que queda en pie en el centro cuando el resto está tachado. */
    remate: string;
    filas?: number;
    /** Frase equivalente para lector de pantalla, ya traducida. */
    lectura: string;
}

/**
 * El muro: diez herramientas que se tachan, y una que queda.
 *
 * Adaptado del «Appear Text» de Originkit, que es una rejilla de la misma
 * palabra repetida que se borra hasta dejar una sola en el centro. Aquí la
 * rejilla la forman las diez herramientas de verdad y lo que queda es la marca.
 *
 * Tres cosas cambian respecto al original, y las tres son deliberadas:
 *
 * 1. VA CON EL SCROLL, NO EN BUCLE. El original repite para siempre, con
 *    `repeat: Infinity`. Eso es movimiento autónomo permanente en mitad de una
 *    página: gasta batería aunque nadie mire y vuelve a plantear el problema
 *    del que acabamos de sacar al vídeo del hero. Atado al scroll, el ritmo lo
 *    pone quien lee.
 *
 * 2. SIN framer-motion. No está instalado y no hace falta: GSAP ya está aquí,
 *    y con ScrollTrigger esto es menos código que con `animate` + `times`.
 *
 * 3. LETRA PEQUEÑA Y TACHÓN FINO. Este fold YA tuvo un muro de diez palabras a
 *    48 px tachadas con líneas amarillas gordas, y el equipo lo señaló como una
 *    de las tres cosas «espantosas» de la mitad de arriba. Lo que fallaba no
 *    era la idea, era el volumen: diez palabras enormes y diez trazos de color
 *    de marca compiten con todo. Aquí las herramientas son TEXTURA —pequeñas,
 *    en gris, repetidas— y el contraste lo pone lo que queda en pie. El tachón
 *    va en el propio color del texto, no en amarillo.
 *
 * Sobre lectores de pantalla: el muro entero es repetición decorativa y va
 * `aria-hidden`. La lista de verdad se lee una sola vez, en el texto oculto.
 */
const Muro: React.FC<Props> = ({ palabras, remate, filas = FILAS, lectura }) => {
    /* Cada renglón arranca en un punto distinto de la lista. Si todos
       empezaran por «Excel.», las mismas palabras quedarían alineadas en
       columna y se leería como una tabla, no como un montón. */
    const renglones = useMemo(
        () =>
            Array.from({ length: filas }, (_, i) =>
                Array.from(
                    { length: POR_FILA },
                    (_, j) => palabras[(i * 3 + j) % palabras.length],
                ),
            ),
        [palabras, filas],
    );

    return (
        <div data-muro className="rp-muro">
            <div aria-hidden="true" className="rp-muro__campo">
                {renglones.map((renglon, i) => (
                    <div key={i} data-muro-fila className="rp-muro__fila">
                        {renglon.map((palabra, j) => (
                            <span
                                key={`${i}-${j}`}
                                data-muro-palabra
                                /* Por dónde se borra. Renglones alternos se
                                   barren en sentidos contrarios: si todos se
                                   fueran hacia el mismo lado, el muro se
                                   vaciaría como una persiana. */
                                data-sentido={i % 2 === 0 ? 'ltr' : 'rtl'}
                                className="rp-muro__palabra"
                            >
                                {palabra}
                                {/* El tachón es un elemento, no `line-through`:
                                    una decoración de texto no se puede animar,
                                    y este tiene que dibujarse de izquierda a
                                    derecha conforme bajas. */}
                                <span data-tachon aria-hidden="true" className="rp-muro__tachon" />
                            </span>
                        ))}
                    </div>
                ))}
            </div>

            {/* Lo único que queda en pie. */}
            <p data-remate className="rp-muro__remate">
                {remate}
            </p>

            {/* La lista de verdad, dicha una vez, para quien no ve el muro. */}
            <span className="rp-oficio__lectura">
                {lectura}
            </span>
        </div>
    );
};

export default Muro;
